// Creator payout setup.
//
// Stripe redirects back here after onboarding with no state attached, so the
// page cannot infer anything from the URL beyond "they came back". It always
// re-reads the account from Stripe to decide what to show — returning from the
// flow does not mean onboarding finished, only that the flow was entered and
// exited. Trusting `?done=1` would show creators a green light while their
// payouts were still blocked.
//
// The two states that matter are "no account yet" and "account exists but
// transfers are not active". Both are resolved by sending the creator through a
// fresh Account Link, which is why one button serves both.

import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle2, ExternalLink, Loader, ShieldCheck, TriangleAlert } from 'lucide-react';
import { gathrxrAuthHeader } from '../utils/quotaHeaders';
import { supabase } from '../config/supabase';
import PageHeader from '../components/PageHeader';
import { MARKET_LINKS } from './Marketplace';

interface Sale {
  id: string;
  listing_id: string | null;
  gross_cents: number;
  creator_cents: number;
  currency: string;
  status: string;
  paid_at: string | null;
  payout_id: string | null;
}

/** Must match PAYOUT_HOLD_DAYS in functions/api/marketplace/_logic.ts. */
const HOLD_DAYS = 7;
const usd = (c: number, cur = 'usd') =>
  new Intl.NumberFormat(undefined, { style: 'currency', currency: cur.toUpperCase() }).format(c / 100);
const day = (iso: string) => new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

/** One line of plain-language status per sale. */
function saleState(s: Sale, payouts: Record<string, string>): { text: string; tone: 'ok' | 'wait' | 'bad' } {
  if (s.status === 'refunded') return { text: 'Refunded — no earnings', tone: 'bad' };
  if (s.status === 'disputed') return { text: 'Disputed by the buyer — on hold', tone: 'bad' };
  if (s.status === 'dispute_lost') return { text: 'Chargeback lost — no earnings', tone: 'bad' };
  if (s.payout_id) {
    const st = payouts[s.payout_id];
    if (st === 'reversed') return { text: 'Reversed after a refund', tone: 'bad' };
    return { text: 'Sent to your Stripe account', tone: 'ok' };
  }
  if (!s.paid_at) return { text: 'Processing', tone: 'wait' };
  const due = new Date(Date.parse(s.paid_at) + HOLD_DAYS * 86_400_000);
  return due > new Date()
    ? { text: `Held until ${day(due.toISOString())}`, tone: 'wait' }
    : { text: 'Ready — sent in the next payout run', tone: 'wait' };
}

interface Status {
  connected: boolean;
  payoutsEnabled: boolean;
  status?: string;
  accountId?: string;
  requirements?: any;
  error?: string;
}

export default function CreatorPayouts() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState<Status | null>(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState('');
  const [signedIn, setSignedIn] = useState<boolean | null>(null);
  const [sales, setSales] = useState<Sale[]>([]);
  const [titles, setTitles] = useState<Record<string, string>>({});
  const [payoutStates, setPayoutStates] = useState<Record<string, string>>({});

  // Earnings come straight from the ledger tables (RLS: a creator reads only
  // their own sales and payouts), so the page cannot disagree with payouts.
  const loadEarnings = useCallback(async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const { data } = await supabase
      .from('marketplace_purchases')
      .select('id, listing_id, gross_cents, creator_cents, currency, status, paid_at, payout_id')
      .eq('creator_id', user.id)
      .order('paid_at', { ascending: false })
      .limit(100);
    const rows = (data as Sale[]) || [];
    setSales(rows);
    const ids = [...new Set(rows.map((r) => r.listing_id).filter(Boolean))] as string[];
    if (ids.length) {
      const { data: ls } = await supabase.from('marketplace_listings').select('id, title').in('id', ids);
      setTitles(Object.fromEntries((ls || []).map((l: any) => [l.id, l.title])));
    }
    const pids = [...new Set(rows.map((r) => r.payout_id).filter(Boolean))] as string[];
    if (pids.length) {
      const { data: ps } = await supabase.from('marketplace_payouts').select('id, status').in('id', pids);
      setPayoutStates(Object.fromEntries((ps || []).map((p: any) => [p.id, p.status])));
    }
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const headers = await gathrxrAuthHeader();
      const res = await fetch('/api/marketplace/connect', { headers });
      const data = await res.json();
      if (!res.ok) {
        setError(data?.error || 'Could not load payout status.');
        setStatus(null);
      } else {
        setStatus(data);
      }
    } catch {
      setError('Could not reach the server.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setSignedIn(!!session);
      if (session) { refresh(); loadEarnings(); }
      else setLoading(false);
    })();
  }, [refresh]);

  // Stripe bounces expired or already-used links back to refresh_url. Starting
  // onboarding again immediately is the documented handling, and without it the
  // creator lands on a page that looks like it did nothing.
  useEffect(() => {
    if (params.get('refresh') === '1' && signedIn) start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedIn]);

  const start = async () => {
    setStarting(true);
    setError('');
    try {
      const headers = await gathrxrAuthHeader();
      const res = await fetch('/api/marketplace/connect', {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: '{}',
      });
      const data = await res.json();
      if (!res.ok || !data?.url) {
        setError(data?.error || 'Could not start payout setup.');
        setStarting(false);
        return;
      }
      // Account Links are single-use and short-lived, so this is a redirect
      // rather than a new tab — a link that sits unopened simply expires.
      window.location.href = data.url;
    } catch {
      setError('Could not reach the server.');
      setStarting(false);
    }
  };

  const pending = status?.connected && !status.payoutsEnabled;

  return (
    <div style={page}>
      <PageHeader
        title="Payouts"
        accent="Get paid."
        subtitle="Connect a Stripe account to sell in the marketplace. Stripe handles your bank details and identity checks directly; GathrXR never sees or stores them."
        hero="/hero-marketplace.webp"
        links={MARKET_LINKS}
      />
      <div className="ph-container" style={{ paddingBottom: 72 }}>
      <div style={{ maxWidth: 720 }}>

        {loading && (
          <div style={card}>
            <Loader size={16} className="spin" /> <span style={{ marginLeft: 8 }}>Checking status…</span>
          </div>
        )}

        {!loading && signedIn === false && (
          <div style={card}>
            <p style={{ margin: 0 }}>Sign in to set up payouts.</p>
          </div>
        )}

        {!loading && signedIn && (
          <>
            {status?.payoutsEnabled && (
              <div style={{ ...card, borderColor: 'rgba(74,222,128,0.35)' }}>
                <div style={row}>
                  <CheckCircle2 size={18} color="#4ade80" />
                  <strong>Payouts are active</strong>
                </div>
                <p style={note}>
                  You can publish listings and receive earnings. Payouts are held
                  for 7 days after each sale to cover refunds and chargebacks.
                </p>
              </div>
            )}

            {pending && (
              <div style={{ ...card, borderColor: 'rgba(251,191,36,0.35)' }}>
                <div style={row}>
                  <TriangleAlert size={18} color="#fbbf24" />
                  <strong>Setup incomplete</strong>
                </div>
                <p style={note}>
                  Stripe still needs information before it can pay you. Picking up
                  where you left off takes a minute.
                </p>
              </div>
            )}

            {!status?.connected && (
              <div style={card}>
                <div style={row}>
                  <ShieldCheck size={18} color="#00c2b3" />
                  <strong>Not connected yet</strong>
                </div>
                <p style={note}>
                  You'll be sent to Stripe to confirm your identity and add a bank
                  account, then returned here.
                </p>
              </div>
            )}

            {!status?.payoutsEnabled && (
              <button onClick={start} disabled={starting} style={primaryBtn}>
                {starting ? <Loader size={15} className="spin" /> : <ExternalLink size={15} />}
                {starting ? 'Opening Stripe…' : status?.connected ? 'Finish payout setup' : 'Set up payouts'}
              </button>
            )}

            {status?.payoutsEnabled && (
              <button onClick={start} disabled={starting} style={secondaryBtn}>
                Update payout details
              </button>
            )}

            <button onClick={refresh} style={{ ...secondaryBtn, marginLeft: 8 }}>
              Refresh status
            </button>

            {error && (
              <p style={{ color: '#ff9b9b', fontSize: 13, marginTop: 14 }}>{error}</p>
            )}

            {sales.length > 0 && (() => {
              const live = sales.filter((x) => x.status === 'paid');
              const sent = live.filter((x) => x.payout_id && payoutStates[x.payout_id] !== 'reversed')
                .reduce((a, x) => a + x.creator_cents, 0);
              const pendingCents = live.filter((x) => !x.payout_id).reduce((a, x) => a + x.creator_cents, 0);
              const cur = sales[0]?.currency || 'usd';
              return (
                <div style={{ ...card, marginTop: 22 }}>
                  <div style={row}><strong>Earnings</strong></div>
                  <div style={{ display: 'flex', gap: 28, margin: '12px 0 6px', flexWrap: 'wrap' }}>
                    <div><div style={statNum}>{usd(pendingCents, cur)}</div><div style={statLabel}>Pending payout</div></div>
                    <div><div style={statNum}>{usd(sent, cur)}</div><div style={statLabel}>Sent to Stripe</div></div>
                  </div>
                  <p style={note}>
                    Each sale is held for {HOLD_DAYS} days, then your share goes to your
                    Stripe account, which pays your bank on its own schedule.
                  </p>
                  <div style={{ marginTop: 14 }}>
                    {sales.map((x) => {
                      const st = saleState(x, payoutStates);
                      return (
                        <div key={x.id} style={saleRow}>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {(x.listing_id && titles[x.listing_id]) || 'Listing'}
                            </div>
                            <div style={{ fontSize: 12, color: st.tone === 'ok' ? '#4ade80' : st.tone === 'bad' ? '#ff9b9b' : 'rgba(255,255,255,0.55)' }}>
                              {x.paid_at ? `${day(x.paid_at)} · ` : ''}{st.text}
                            </div>
                          </div>
                          <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                            <div>{usd(x.creator_cents, x.currency)}</div>
                            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>of {usd(x.gross_cents, x.currency)}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {status?.accountId && (
              <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: 11, marginTop: 22 }}>
                Stripe account {status.accountId} · status {status.status}
              </p>
            )}
          </>
        )}
      </div>
      </div>
    </div>
  );
}

const page: React.CSSProperties = {
  minHeight: '100vh',
  background: 'var(--gx-bg, #0a0e1a)',
  color: 'var(--gx-text, #fff)',
};
const card: React.CSSProperties = {
  background: 'rgba(255,255,255,0.03)',
  border: '1px solid var(--gx-border, rgba(255,255,255,0.1))',
  borderRadius: 14,
  padding: '16px 18px',
  marginBottom: 18,
};
const row: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 9, fontSize: 15 };
const note: React.CSSProperties = {
  color: 'rgba(255,255,255,0.6)', fontSize: 13, lineHeight: 1.6, margin: '10px 0 0',
};
const statNum: React.CSSProperties = { fontSize: 22, fontWeight: 700 };
const statLabel: React.CSSProperties = { fontSize: 12, color: 'rgba(255,255,255,0.55)' };
const saleRow: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between', gap: 12, padding: '10px 0',
  borderTop: '1px solid var(--gx-border, rgba(255,255,255,0.08))', fontSize: 13.5,
};
const primaryBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 8,
  padding: '11px 18px', borderRadius: 10, border: 'none', cursor: 'pointer',
  background: 'linear-gradient(90deg, #00c2b3, #7ad0c0)', color: '#04212a',
  fontSize: 14, fontWeight: 600,
};
const secondaryBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 8,
  padding: '11px 16px', borderRadius: 10, cursor: 'pointer',
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid var(--gx-border, rgba(255,255,255,0.12))',
  color: 'rgba(255,255,255,0.85)', fontSize: 13,
};
